import React, { useState, useEffect } from "react";
import { GetAllCities } from "../../../Services/AddressService";

export default function CreateAddressPopup({ onClose, onSave, editingAddress }) {
    const email = localStorage.getItem("email");

    const [citySearchTerm, setCitySearchTerm] = useState("");
    const [selectedCity, setSelectedCity] = useState(null);
    const [cities, setCities] = useState([]);
    const [isCityPrefilled, setIsCityPrefilled] = useState(false);
    const [form, setForm] = useState({
        firstName: "",
        lastName: "",
        email: email || "",
        country: "",
        state: "",
        city: "",
        address1: "",
        address2: "",
        zipPostalCode: "",
        phoneNumber: "",
        addressType: "1", // Default Home = 1
        isDefault: false,
    });

    // Fetch cities
    const fetchCities = async (term = "") => {
        try {
            const response = await GetAllCities({ city: term });
            const list = response.data?.data || response.data || [];
            if (Array.isArray(list)) {
                setCities(list);
            } else {
                setCities([]);
            }
        } catch (error) {
            console.error("Error fetching cities:", error);
            setCities([]);
        }
    };

    // Debounced city search
    useEffect(() => {
        if (selectedCity) return;
        if (!citySearchTerm) {
            setCities([]);
            return;
        }
        // Don't fetch cities if city is pre-filled for editing
        if (editingAddress && isCityPrefilled && citySearchTerm === editingAddress.cityName) {
            return;
        }
        const timeout = setTimeout(() => fetchCities(citySearchTerm), 400);
        return () => clearTimeout(timeout);
    }, [citySearchTerm, selectedCity, editingAddress, isCityPrefilled]);

    // Pre-fill form when editing
    useEffect(() => {
        if (editingAddress) {
            setForm({
                firstName: editingAddress.firstName || "",
                lastName: editingAddress.lastName || "",
                email: editingAddress.email || email || "",
                country: editingAddress.countryName || "",
                state: editingAddress.stateName || "",
                city: editingAddress.cityName || "",
                address1: editingAddress.address1 || "",
                address2: editingAddress.address2 || "",
                zipPostalCode: editingAddress.zipPostalCode || "",
                phoneNumber: editingAddress.phoneNumber || "",
                addressType: editingAddress.addressType?.toString() || "1",
                isDefault: editingAddress.isDefault || false,
            });
            setCitySearchTerm(editingAddress.cityName || "");
            setIsCityPrefilled(true);
        } else {
            setIsCityPrefilled(false);
        }
    }, [editingAddress, email]);

    // City select handler
    const handleCitySelect = (city) => {
        setSelectedCity(city);
        setCitySearchTerm(city.name);
        setCities([]);
        setForm((prev) => ({
            ...prev,
            city: city.name,
            state: city.stateName,
            country: city.countryName,
        }));
    };

    // Input change handler
    const handleChange = (e) => {
        const { name, value, type, checked } = e.target;
        setForm((prev) => ({
            ...prev,
            [name]: type === "checkbox" ? checked : value,
        }));
    };

    return (
        <div className="modal show fade" style={{ display: "block" }}>
            <div className="modal-dialog modal-lg modal-dialog-centered"
                style={{
                    padding: "0 20px",      // left-right padding
                    borderRadius: "12px",   // rounded corners
                }}>
                <div className="modal-content"
                    style={{
                        borderRadius: "12px", // ensure content also rounded
                    }}>

                    {/* HEADER */}
                    <div className="modal-header">
                        <h5 className="modal-title">{editingAddress ? "Edit Address" : "Add New Address"}</h5>
                        <button type="button" className="btn-close" onClick={onClose}></button>
                    </div>

                    {/* BODY */}
                    <div className="modal-body">
                        <div className="row g-3">

                            {/* Name & Phone */}
                            <div className="col-md-4">
                                <input
                                    name="firstName"
                                    className="form-control"
                                    placeholder="First Name"
                                    value={form.firstName}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-md-4">
                                <input
                                    name="lastName"
                                    className="form-control"
                                    placeholder="Last Name"
                                    value={form.lastName}
                                    onChange={handleChange}
                                />
                            </div>

                            <div className="col-md-4">
                                <input
                                    name="phoneNumber"
                                    className="form-control"
                                    placeholder="Phone Number"
                                    value={form.phoneNumber}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Address Line */}
                            <div className="col-12">
                                <textarea
                                    name="address1"
                                    className="form-control"
                                    placeholder="Address Line 1"
                                    rows={2}
                                    value={form.address1}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* City Search */}
                            <div className="col-md-4 position-relative">
                                <input
                                    type="text"
                                    className="form-control"
                                    placeholder="Search city"
                                    value={citySearchTerm}
                                    onChange={(e) => {
                                        setCitySearchTerm(e.target.value);
                                        setSelectedCity(null);
                                    }}
                                    required
                                />
                                {cities.length > 0 && (
                                    <ul
                                        className="list-group position-absolute w-100"
                                        style={{ top: "100%", zIndex: 2000, maxHeight: "200px", overflowY: "auto" }}
                                    >
                                        {cities.map((city) => (
                                            <li
                                                key={city.id}
                                                className="list-group-item list-group-item-action"
                                                onClick={() => handleCitySelect(city)}
                                                style={{ cursor: "pointer" }}
                                            >
                                                {city.name}
                                            </li>
                                        ))}
                                    </ul>
                                )}
                            </div>

                            {/* State & Country */}
                            <div className="col-md-4">
                                <input
                                    className="form-control"
                                    placeholder="State"
                                    value={form.state}
                                    readOnly
                                />
                            </div>

                            <div className="col-md-4">
                                <input
                                    className="form-control"
                                    placeholder="Country"
                                    value={form.country}
                                    readOnly
                                />
                            </div>

                            {/* Zip Code */}
                            <div className="col-md-4">
                                <input
                                    name="zipPostalCode"
                                    className="form-control"
                                    placeholder="Zip Code"
                                    value={form.zipPostalCode}
                                    onChange={handleChange}
                                />
                            </div>

                            {/* Address Type */}
                            <div className="col-12">
                                <label className="  mb-2" style={{ fontSize: "18px", fontWeight: 500 }}>Address Type:</label>
                                <div className="d-flex gap-3 mt-1">
                                    {[
                                        { label: "Home", value: "1" },
                                        { label: "Office", value: "2" },
                                        { label: "Other", value: "3" },
                                    ].map((type) => (
                                        <div key={type.value} className="form-check">
                                            <input
                                                className="form-check-input"
                                                type="radio"
                                                name="addressType"
                                                value={type.value}
                                                checked={form.addressType === type.value}
                                                onChange={handleChange}
                                                id={`type-${type.value}`}

                                                style={{
                                                  
                                                    /* remove blue hover / blue focus ring */
                                                    outline: "none",
                                                    boxShadow: "none",

                                                    border: "2px solid #a37c4d", // outer ring
                                                    borderRadius: "50%",         // circle

                                                    display: "inline-flex",
                                                    alignItems: "center",
                                                    justifyContent: "center",

                                                    /* fill inner dot when selected */
                                                    backgroundColor: form.addressType === type.value ? "#a37c4d" : "transparent",

                                                }}
                                            />
                                            <label className="form-check-label" htmlFor={`type-${type.value}`}>
                                                {type.label}
                                            </label>
                                        </div>
                                    ))}
                                </div>
                            </div>

                            {/* Default Address */}
                            <div className="col-12 d-flex align-items-center gap-2">
                                <input
                                    id="isDefault"
                                    className="form-check-input"
                                    type="checkbox"
                                    name="isDefault"
                                    checked={form.isDefault}
                                    onChange={handleChange}
                                    style={{
                                        border: "2px solid #a37c4d",
                                        backgroundColor: form.isDefault ? "#a37c4d" : "transparent", // checked color
                                        outline: "none",
                                        boxShadow: "none",
                                        WebkitTapHighlightColor: "transparent",
                                    }}
                                />
                                <label className="form-check-label" htmlFor="isDefault" style={{ cursor: "pointer" }}>
                                    Set as Default Address
                                </label>
                            </div>
                        </div>
                    </div>

                    {/* FOOTER */}
                    <div className="modal-footer">
                        <button className="btn btn-secondary" onClick={onClose}
                            style={{
                                backgroundColor: "#d6c4aa",   // theme-compatible light shade
                                color: "#333", 
                                border: "none",
                                padding: "8px 20px",
                                borderRadius: "6px",
                            }}>
                            Cancel
                        </button>
                        <button
                            className="btn btn-primary"
                            onClick={() => {
                                if (!selectedCity && !editingAddress) {
                                    alert("Please select a valid city from the dropdown.");
                                    return;
                                }

                                // Remove city/state/country names
                                const { city, state, country, ...rest } = form;

                                const payload = {
                                    ...rest,
                                    cityId: selectedCity?.id || editingAddress?.cityId,
                                    stateId: selectedCity?.stateId || editingAddress?.stateId,
                                    countryId: selectedCity?.countryId || editingAddress?.countryId,
                                    addressType: parseInt(rest.addressType), // ✅ Convert to integer
                                };

                                const success = onSave(payload); // ✅ await here
                                if (success) {
                                    onClose(); // ✅ close popup
                                }
                            }}
                            style={{
                                backgroundColor: "#a37c4d",   // theme color
                                color: "#fff",
                                border: "none",
                                padding: "8px 20px",
                                borderRadius: "6px",
                            }}
                        >
                            {editingAddress ? "Update" : "Save"}
                        </button>
                    </div>

                </div>
            </div>
        </div>
    );
}